'use client';

import { useTranslation } from '@/lib/i18n';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import Image from 'next/image';
import useInitSystemWallet from '../hooks/use-system-wallet';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { cn } from '@/lib/utils';

interface CreateWalletProps {
  /** 抽屉侧边栏场景（窄容器，约 600px），改用紧凑竖排布局，插图不溢出 */
  openInSidebar?: boolean;
}

export default function CreateWallet({ openInSidebar }: CreateWalletProps) {
  const { t, currentLanguage } = useTranslation();
  const { showError } = useSnackbar();
  const { init, isOpening, error } = useInitSystemWallet();

  const handleCreate = async () => {
    const res = await init();
    if (res?.status === 'failed') {
      showError(t('There was an error creating the wallet. Please try again later.'));
    }
  };

  const button = (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-full bg-white font-semibold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60',
        openInSidebar ? 'h-11 px-6 text-sm' : 'h-12.5 px-6 text-base',
      )}
      onClick={handleCreate}
      disabled={isOpening}
    >
      {isOpening ? (
        <Loader2 className="animate-spin" size={18} />
      ) : (
        <>
          {t('Create account')}
          <ArrowUpRight size={18} className="text-[#8B3FFC]" />
        </>
      )}
    </button>
  );

  const errorMessage = !!error && (
    <p className="mt-3 text-sm text-red-500">{t('Failed to create wallet. Please try again.')}</p>
  );

  if (openInSidebar) {
    return (
      <div className="flex w-full flex-col items-start overflow-hidden rounded-2xl bg-black p-6">
        <h3 className="text-xl font-semibold text-white">{t('Create Your Polymarket Account')}</h3>
        <p className="mt-2 text-sm leading-relaxed text-[#B7B9BC]">
          {t('Set up your Polymarket account to add funds, trade markets, and run strategies.')}
        </p>
        <div className="relative mt-4 h-40 w-full overflow-hidden rounded-xl">
          <Image src="/image/wallet-hero.png" alt="" fill className="object-cover" />
        </div>
        {errorMessage}
        <div className="mt-5">{button}</div>
      </div>
    );
  }

  return (
    <section className="relative overflow-hidden rounded-3xl bg-black h-[calc(100vh-60px)] -mx-6">
      {/* Hero 插画：铺满右侧，向右溢出裁切 */}
      <img
        src="/image/wallet-hero.png"
        alt=""
        className="absolute -translate-y-1/2 top-1/2 right-0"
        // priority={false}
      />

      {/* 左侧深色遮罩，保证文字可读 */}
      {/* <div className="absolute inset-0 bg-[linear-gradient(90deg,#040305_0%,#040305_56%,rgba(4,3,5,0)_100%)]" /> */}

      {/* 文字内容 */}
      <div className="relative z-10 max-w-xl ml-50 mt-40">
        {currentLanguage === 'en' ? (
          <h2 className="flex flex-col text-[32px] font-normal leading-tight text-white md:text-[52px]">
            <span>{t('Create Your')}</span>
            <span className="text-[#A772FF]">{t('Polymarket Account')}</span>
          </h2>
        ) : (
          <h2 className="flex flex-col text-[32px] font-normal leading-tight text-white md:text-[52px] w-112.5">
            <span>{t('Create Your Polymarket Account')}</span>
          </h2>
        )}
        <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#B7B9BC] md:mt-6 md:text-base">
          {t('Set up your Polymarket account to add funds, trade markets, and run strategies.')}
        </p>
        {errorMessage}
        <div className="mt-8 md:mt-9">{button}</div>
      </div>
    </section>
  );
}
